import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { listFormSubmissions, deleteFormSubmission } from "../../../api/cms";
import { formSubmissionForms } from "../../../utils/form-submissions";

const DETAIL_FIELDS = [
  ["Salutation", "salutation"],
  ["Date of birth", "dateOfBirth"],
  ["Nationality", "nationality"],
  ["Telephone", "telephone"],
  ["Job title", "jobTitle"],
  ["Company", "companyName"],
  ["Website", "website"],
  ["Billing name", "billingName"],
  ["VAT", "billingVat"],
  ["Billing address", "billingAddress"],
];

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminFormSubmissionsForm() {
  const { form } = useParams();
  const config = formSubmissionForms.find((f) => f.id === form);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    listFormSubmissions(form)
      .then((data) => setSubmissions([...data].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))))
      .catch(() => setError("Failed to load submissions"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [form]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await deleteFormSubmission(id);
    load();
  };

  if (!config) return <p className="text-red-500">Unknown form.</p>;
  if (loading) return <p>Loading…</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div>
      <div className="text-sm text-gray-500 mb-2">
        <Link to="/admin/form-submissions" className="hover:underline">Form submissions</Link> / {config.label}
      </div>
      <h1 className="text-2xl font-bold mb-6">{config.label} <span className="text-base font-normal text-gray-500">({submissions.length})</span></h1>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Submitted</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">E-mail</th>
              <th className="px-4 py-3 font-semibold">Company</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {submissions.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No submissions yet.</td></tr>
            )}
            {submissions.map((s) => (
              <FragmentRow key={s.id} s={s} open={expanded === s.id} onToggle={() => setExpanded(expanded === s.id ? null : s.id)} onDelete={() => handleDelete(s.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({ s, open, onToggle, onDelete }) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(s.createdAt)}</td>
        <td className="px-4 py-3">{s.firstName} {s.familyName}</td>
        <td className="px-4 py-3"><a href={`mailto:${s.email}`} onClick={(e) => e.stopPropagation()} className="underline">{s.email}</a></td>
        <td className="px-4 py-3">{s.companyName}</td>
        <td className="px-4 py-3 text-right">
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600 hover:underline">Delete</button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-4 py-4">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {DETAIL_FIELDS.map(([label, key]) => (
                <div key={key}>
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="whitespace-pre-line">{s[key] || "—"}</dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}
